"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function LEssenceDeNotreMission() {
  const [language, setLanguage] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("lacolonne_language") || "fr";
    }
    return "fr";
  });
  useEffect(() => {
    const handleLanguageUpdate = () => {
      const currentLang = localStorage.getItem("lacolonne_language") || "fr";
      setLanguage(currentLang);
    };
    window.addEventListener("storage", handleLanguageUpdate);
    window.addEventListener("languagechange", handleLanguageUpdate);
    return () => {
      window.removeEventListener("storage", handleLanguageUpdate);
      window.removeEventListener("languagechange", handleLanguageUpdate);
    };
  }, []);
  const isEnglish = language === "en";
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="2834" id="lessence-de-notre-mission" className="relative w-full overflow-hidden dark bg-background text-foreground py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Content Column */}
          <div ref={contentRef} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-secondary font-semibold mb-6">
              <span className="inline-block w-8 h-px bg-secondary" />
              <span>{isEnglish ? "OUR PHILOSOPHY — 01" : "NOTRE PHILOSOPHIE — 01"}</span>
            </div>

            {/* Bolder Heading */}
            <h2 className="font-default font-extrabold text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mb-8">
              {isEnglish ? <>
                  Building{" "}
                  <em className="italic text-secondary font-serif font-normal">bridges</em>.
                  <br />
                  Cultivating{" "}
                  <em className="italic text-secondary font-serif font-normal">dignity</em>.
                </> : <>
                  Bâtir des{" "}
                  <em className="italic text-secondary font-serif font-normal">ponts</em>.
                  <br />
                  Cultiver la{" "}
                  <em className="italic text-secondary font-serif font-normal">dignité</em>.
                </>}
            </h2>

            {/* Description Paragraphs */}
            <div className="max-w-xl space-y-6 font-default text-base sm:text-lg leading-relaxed text-foreground/85">
              <p>
                {isEnglish ? "At Association La Colonne, we believe that successful integration relies on solid community support. We act as a pillar for immigrant seniors and francophone families in Edmonton." : "À l'Association La Colonne, nous croyons que l'intégration réussie repose sur un soutien communautaire solide. Nous agissons comme un pilier pour les aînés immigrants et les familles francophones d'Edmonton."}
              </p>
              <p>
                {isEnglish ? "Every initiative is a step toward inclusion. We design spaces where generations connect, where culture thrives, and where digital literacy becomes an empowering tool for everyone." : "Chaque initiative est un pas vers l'inclusion. Nous concevons des espaces où les générations se rencontrent, où la culture s'exprime et où l'alphabétisation numérique devient un outil d'émancipation pour tous."}
              </p>
            </div>

            {/* Accent Divider */}
            <div className="mt-10 mb-8">
              <div className={`h-0.5 bg-secondary transition-all duration-[1400ms] ease-out ${contentVisible ? "w-32" : "w-0"}`} />
            </div>

            {/* Action Link */}
            <Link to="/OurProgramsPage" className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-2xl bg-card border border-border text-foreground hover:text-secondary hover:border-secondary transition-all duration-300 font-mono text-xs tracking-widest uppercase shadow-md w-fit">
              <span>{isEnglish ? "Discover our programs" : "Découvrir nos programmes"}</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.5} />
            </Link>
          </div>

          {/* Image Column with Rounded-3xl Card Treatment */}
          <div ref={imageRef} className={`lg:col-span-5 relative w-full transition-all duration-[1200ms] ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative w-full aspect-[4/5] lg:h-[620px] rounded-3xl overflow-hidden border border-border/60 shadow-2xl bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/photo de groupe .jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/photo de groupe .jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/photo de groupe .jpg-wvc-sizes" alt={isEnglish ? "Intergenerational gathering in Edmonton - Association La Colonne" : "Rencontre intergénérationnelle à Edmonton - Association La Colonne"} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale-[15%] contrast-[1.05] transition-transform duration-700 hover:scale-105" />

              {/* Gradient Scrim & Caption Badge */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />

              <div className="absolute bottom-6 left-6 right-6 p-4 rounded-2xl bg-background/80 backdrop-blur-md border border-border/50 shadow-md flex items-center justify-between">
                <span className="font-mono text-xs tracking-widest uppercase text-foreground">
                  {isEnglish ? "/ Edmonton Community — 2024" : "/ Communauté Edmonton — 2024"}
                </span>
                <span className="w-2 h-2 rounded-lg bg-secondary" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}